/**
 * 레인 흐름도 격자 좌표 -> px 변환 — 순수 함수만 둔다.
 *
 * 배치(어느 레인·몇 번째 칸, 재고↔공정 이분 그래프의 층·경유점·포트 순번)는 floLayout.js 가 정한다.
 * floModel.js 가 그 결과를 격자 좌표(c, y, w, po, pi, ch)로 담아 낸다.
 * 이 파일은 그 격자 좌표에 치수 상수를 곱해 그리기만 하고 순서를 다시 정하지 않는다.
 *
 * 세로 단위(U) 치수의 유일한 소유자다.
 *   floModel.js 의 노드 크기 배정(INV_H_U 등)이 이 파일의 export 를 그대로 가져다 쓴다.
 *   가로 치수(BOX_W·GAP·COL_W, 도형 크기)는 그리기 전용 값이라 배치와는 무관하다:
 *   U=12px, LANE_PAD=8px, GAP=64px, BOX_W=200px, COL_W=BOX_W+GAP.
 *   열 c 의 칸은 [x0, x0+BOX_W](라벨 폭·간격 계산용), 그 뒤 틈은 [x0+BOX_W, x0+COL_W].
 *   레인 이름 칸(옛 HEADER_W)은 이제 이 SVG 밖의 고정 거터로 옮겼다.
 *   폭 상수(FLO_GUTTER_W)는 floTokens.js, 렌더는 FloViewOverlay.js 몫이다.
 *   첫 열의 x0 은 GAP/2 + lead 다.
 *   도형(삼각형·공정 상자·설비 타원)은 칸 안에서 고정 크기로 앵커선(yA)에 중심을 맞춘다 —
 *   FLO 차트(mp-model/FloNodes.js)처럼 도형과 칸 폭이 분리돼 있다.
 *   재고/집약은 항목 6.0U 안에 선을 1.75U 지점에, 공정(키팅·운송·라우트 없는 생산)도 6.0U/1.75U,
 *   설비 달린 생산은 10.5U 안 6.0U 지점에 둔다.
 *
 * 엣지 경로는 FLO 의 smoothstep(직교+둥근 모서리)을 흉내 낸다 — 베지어는 쓰지 않는다.
 *   상자 밖 어디서든 순수 수평·수직 선분만 있고, 방향이 바뀌는 자리(모서리)만 작게 둥글린다.
 *   세로로 방향이 바뀌는 지점(트랙)은 늘 그 틈 안에 있어 상자를 관통할 수 없다.
 *   같은 틈에서 여러 호가 방향을 바꾸면 트랙 x 를 (하행/상행으로 묶어 교차가 적은 순서로) 벌려
 *   나란한 선이 한 다발로 겹쳐 보이지 않게 한다 — 틈 폭 자체도 그만큼 늘어난다(assignTracks 절).
 *   한 목표로 모이는(또는 한 출발에서 갈라지는) 호는 세로 구간이 안 겹치면 한 트랙을 나눠 써 같은 x 에서 꺾인다.
 *   같은 y 에서 수평 선분끼리 겹치는 "공선 겹침"은 별도로 감지해 포트를 살짝(4px) 밀어 피한다.
 *
 * 중립 leaf — pages/** 를 import 하지 않는다(TreeTable.js 와 같은 규약).
 */

// ── 격자 상수(세로 U 단위는 이 파일이 정본, 가로·도형 크기는 그리기 전용) ────
export const U = 12;
export const LANE_PAD = 8;
export const GAP = 64;
export const BOX_W = 200;
export const COL_W = BOX_W + GAP;

// U 단위 높이 상수 — floModel.js 의 노드 크기 배정이 이 값을 그대로 가져다 쓴다.
export const INV_H_U = 6.0;
export const INV_ANCHOR_U = 1.75;
export const PROC_H_U = 6.0;
export const PROC_ANCHOR_U = 1.75;
export const RES_H_U = 10.5;
export const RES_ANCHOR_U = 6.0;

// 재고/집약 — 항목 6.0U 안에 선을 1.75U 지점에 둔다.
export const INV_ITEM_H = INV_H_U * U;
export const INV_ANCHOR = INV_ANCHOR_U * U;
// 공정(키팅·운송·라우트 없는 생산) — 재고와 값이 같다.
export const PROC_ITEM_H = PROC_H_U * U;
export const PROC_ANCHOR = PROC_ANCHOR_U * U;
// 설비(자원) 딸린 생산.
export const RES_ITEM_H = RES_H_U * U;
export const RES_ANCHOR = RES_ANCHOR_U * U;

// 도형 크기(칸 폭 BOX_W 와 무관, 앵커선에 중심) — FLO 참고 이미지 비례.
export const TRI_W = 42;
export const TRI_H = 36;
export const PROC_BOX_W = 64;
export const PROC_BOX_H = 36;
export const RES_OVAL_W = 64;
export const RES_OVAL_H = 28;
export const RES_OVAL_TOP_PAD = 3; // 오벌 위쪽 끝 = itemTop + 이 값

// 포트 — 도형 높이(36) - 6 안에서 퍼진다. 두 도형(삼각형·상자)이 같은 높이라 band 도 하나다.
export const PORT_BAND = PROC_BOX_H - 6;
// 출발(departure) 포트 간격 상한 — 기존 값 그대로.
export const PORT_STEP_MAX = 6;
// 도착(arrival) 포트 간격 상한 — 화살촉(9px 안팎)·칩이 옆 포트와 겹치지 않도록 출발보다 넓게 둔다.
export const PORT_STEP_MAX_IN = 12;

// 트랙(세로 방향전환 x) — 같은 틈에서 벌리는 간격과 상자 경계 여유. 라운드3 스태거와 같은 값.
export const CURVE_STAGGER = 8;
export const CURVE_MARGIN = 4;
// 모서리 둥글림 반지름 상한(FLO smoothstep borderRadius 흉내).
export const CORNER_R = 5;
// 공선(같은 y) 겹침을 가르는 문턱과, 해소용 포트 밀기 폭.
export const COLLINEAR_EPS = 1.5;
export const NUDGE = 4;
// 한 트랙을 나눠 쓰는 방향전환끼리 세로 구간이 떨어져 있어야 하는 최소 간격.
export const TRACK_SHARE_GAP = 6;

// 라벨.
export const LABEL_MAX_W = 192;
export const CHIP_H = 16;
export const CHIP_PAD_X = 8;
// 한 목표 노드로 도착 칩이 몰려 두 열로 갈릴 때, 두 열 사이에 두는 여백.
export const CHIP_COL_GAP = 4;

/** 노드 표시 분류 — 치수 계산과 그리기가 공유하는 기준. */
export function nodeShape(node) {
  if (node.t === "inv" || node.t === "agg") return "inv";
  if (node.t === "make" && node.res) return "res";
  return "proc"; // kit · move · 라우트 없는 make
}

/** 문자 수 기반 폭 추정 — canvas 측정을 못 붙이는 순수 계산(테스트)용 기본값. */
function fallbackMeasure(text) {
  return String(text ?? "").length * 7 + 4;
}

function clamp(v, lo, hi) {
  return lo <= hi ? Math.min(Math.max(v, lo), hi) : (lo + hi) / 2;
}

/**
 * 꼭짓점이 위인 삼각형(재고/집약)의 빗변 x — 포트 y 에 따라 값이 다르다.
 * 삼각형은 앵커선(yA)에 세로 중심을 맞춘다: 꼭짓점(cx, yA-TRI_H/2)에서
 * 밑변 양끝(cx∓TRI_W/2, yA+TRI_H/2)까지를 y 로 선형보간한다.
 */
export function invSideX(cx, yA, side, y) {
  const half = TRI_H / 2;
  const t = clamp((y - (yA - half)) / TRI_H, 0, 1);
  const halfW = TRI_W / 2;
  return side === "right" ? cx + halfW * t : cx - halfW * t;
}

/** 노드 한쪽 면의 포트 x — 공정/설비는 상자 경계(cx∓32) 고정, 재고/집약은 포트 y 에 따라 빗변을 따라간다. */
function sideX(g, side, y) {
  if (g.shape !== "inv") return side === "right" ? g.cx + PROC_BOX_W / 2 : g.cx - PROC_BOX_W / 2;
  return invSideX(g.cx, g.yA, side, y);
}

/** 레인 배치 — h(U, 채널 포함) 를 px 로 쌓는다. 레인 위치는 위에서부터 누적이다. */
function layoutLanes(lanes) {
  let top = 0;
  const out = (lanes || []).map((lane) => {
    const height = (lane.h || 0) * U + LANE_PAD * 2;
    const g = { ...lane, top, height };
    top += height;
    return g;
  });
  return { lanes: out, laneByKey: Object.fromEntries(out.map((l) => [l.k, l])), totalHeight: top };
}

/** 틈 하나의 폭 — 방향전환이 하나 이하면 기본 GAP, 그 이상이면 트랙 간격만큼 늘어난다. */
function gapWidthFor(n) {
  if (!n || n <= 1) return GAP;
  return Math.max(GAP, (n - 1) * CURVE_STAGGER + 2 * CURVE_MARGIN);
}

/**
 * 실제 노드 x 를 정하기 전에, 위상(열 번호)만으로 각 틈을 지나는 방향전환 수를 센다 — 틈 폭이
 * 그 수에 달려 있고, 그 수는 실제 경로를 만들어야 정확히 아는 값이라 위상만 보는 예비 집계로 끊는다.
 * 정방향 호는 s.c 열부터 t.c-1 열까지(서버가 사이 열마다 경유점을 채우므로 한 칸도 안 건너뛴다)
 * 각 틈에 1개씩 놓는다. 되돌이 호는 출발 다음 틈(s.c)과 도착 앞 틈(t.c-1) 두 곳에 1개씩이다.
 * 도착 열이 0 이면 그 "앞 틈"은 그림 왼쪽 바깥(인덱스 -1, lead), 출발 열이 맨 오른쪽이면 "다음 틈"은
 * 그림 오른쪽 바깥(인덱스 maxCol, trail)이다.
 */
function countGapDemand(rawNodes, edges) {
  const colOf = {};
  for (const n of rawNodes) colOf[n.k] = n.c || 0;
  const count = new Map();
  const bump = (idx) => count.set(idx, (count.get(idx) || 0) + 1);
  for (const e of edges || []) {
    const sc = colOf[e.s];
    const tc = colOf[e.t];
    if (sc == null || tc == null) continue;
    if (e.back) {
      bump(sc);
      bump(tc - 1);
    } else {
      for (let c = sc; c < tc; c += 1) bump(c);
    }
  }
  return count;
}

/**
 * spec -> 레인 px, 노드별 중심·모양 좌표, 포트 카운트까지. 엣지 경로는 buildEdges 가 따로 만든다
 * (좌표만 필요한 호출부와 칩까지 필요한 호출부를 갈라 두면 테스트가 단계별로 좁혀진다).
 *
 * 열 x(colX0)는 c*COL_W 가 아니라 누적이다 — 열 c 뒤 틈의 폭이 그 틈을 지나는 방향전환 수에 따라
 * 늘어날 수 있어서, 앞선 모든 틈의 실제 폭을 더해야 다음 열의 자리가 나온다.
 */
export function computeGeometry(spec) {
  const { lanes, laneByKey, totalHeight } = layoutLanes(spec?.lanes);
  const rawNodes = spec?.nodes || [];
  let maxCol = 0;
  for (const n of rawNodes) if ((n.c || 0) > maxCol) maxCol = n.c || 0;

  const demand = countGapDemand(rawNodes, spec?.edges);
  // lead·trail(그림 바깥 가상 틈)은 되돌이 호가 실제로 그곳을 쓸 때만 자리를 연다.
  const leadCount = demand.get(-1) || 0;
  const trailCount = demand.get(maxCol) || 0;
  const lead = leadCount > 0 ? gapWidthFor(leadCount) : 0;
  const trail = trailCount > 0 ? gapWidthFor(trailCount) : 0;

  const colXArr = [GAP / 2 + lead];
  for (let c = 0; c < maxCol; c += 1) {
    colXArr.push(colXArr[c] + BOX_W + gapWidthFor(demand.get(c) || 0));
  }
  const colX0 = (c) => colXArr[c];
  const gapWidthAt = (c) => {
    if (c < 0) return lead;
    if (c >= maxCol) return trail;
    return colXArr[c + 1] - (colXArr[c] + BOX_W);
  };

  const outCount = {};
  const inCount = {};
  for (const e of spec?.edges || []) {
    outCount[e.s] = (outCount[e.s] || 0) + 1;
    inCount[e.t] = (inCount[e.t] || 0) + 1;
  }

  const nodes = {};
  for (const n of rawNodes) {
    const lane = laneByKey[n.lane];
    if (!lane) continue;
    const shape = nodeShape(n);
    const c = n.c || 0;
    const x0 = colX0(c);
    const x1 = x0 + BOX_W;
    const cx = (x0 + x1) / 2;
    const yA = lane.top + LANE_PAD + (n.y || 0) * U;
    const g = {
      key: n.k, node: n, shape, lane, c, x0, x1, cx, yA,
      outN: outCount[n.k] || 0, inN: inCount[n.k] || 0,
    };
    if (shape === "inv") {
      g.itemTop = yA - INV_ANCHOR;
      g.itemBottom = g.itemTop + INV_ITEM_H;
      g.triTop = yA - TRI_H / 2;
      g.triBottom = yA + TRI_H / 2;
    } else if (shape === "res") {
      g.itemTop = yA - RES_ANCHOR;
      g.itemBottom = g.itemTop + RES_ITEM_H;
      g.boxTop = yA - PROC_BOX_H / 2;
      g.boxBottom = yA + PROC_BOX_H / 2;
      g.ovalTop = g.itemTop + RES_OVAL_TOP_PAD;
      g.ovalBottom = g.ovalTop + RES_OVAL_H;
    } else {
      g.itemTop = yA - PROC_ANCHOR;
      g.itemBottom = g.itemTop + PROC_ITEM_H;
      g.boxTop = yA - PROC_BOX_H / 2;
      g.boxBottom = yA + PROC_BOX_H / 2;
    }
    nodes[n.k] = g;
  }

  const rightmostX1 = colX0(maxCol) + BOX_W;
  const width = rightmostX1 + GAP / 2 + trail;

  return {
    dir: spec?.dir, lanes, laneByKey, nodes, colX0, gapWidthAt, maxCol,
    width, height: totalHeight, lead, trail,
  };
}

/** 노드 전체 시각 영역(라벨 칩 포함) — 엣지가 이 사각형을 지나면 관통으로 본다. */
export function nodeBBox(g) {
  if (!g) return null;
  return { x0: g.x0, x1: g.x1, top: g.itemTop, bottom: g.itemBottom };
}

/**
 * 포트 세로 위치 — n<=1 이면 선 그대로, 아니면 band 안에서 균등 배치(대칭).
 * side="in"(도착)은 화살촉·칩 겹침을 피하려 간격 상한이 출발보다 넓다(PORT_STEP_MAX_IN).
 */
export function portY(g, idx, n, side = "out") {
  if (!g || !n || n <= 1) return g?.yA ?? 0;
  const stepMax = side === "in" ? PORT_STEP_MAX_IN : PORT_STEP_MAX;
  const step = Math.min(stepMax, PORT_BAND / (n - 1));
  return g.yA + (idx - (n - 1) / 2) * step;
}

/** 포트 최소 간격 — portY 가 실제로 벌리는 값과 같은 식이다(스냅 허용 판정의 기준). */
function minPortGap(side, n) {
  if (!n || n <= 1) return 0;
  const stepMax = side === "in" ? PORT_STEP_MAX_IN : PORT_STEP_MAX;
  return Math.min(stepMax, PORT_BAND / (n - 1));
}

function round2(v) {
  return Math.round(v * 100) / 100;
}
function sign(v) {
  return v > 0 ? 1 : (v < 0 ? -1 : 0);
}
function dist(a, b) {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y); // 축정렬 점 사이는 맨해튼 거리 = 실제 길이
}

/**
 * 직교 점 열(연속한 두 점은 늘 수평이거나 수직) -> SVG path d. 방향이 바뀌는 안쪽 점마다
 * 반지름 min(CORNER_R, 앞뒤 구간 길이의 절반)만큼 둥글린다(FLO smoothstep 흉내) — 베지어(C)는
 * 전혀 쓰지 않는다. 구간 길이가 반지름의 2배보다 짧으면(|dy| 등이 아주 작으면) 그 모서리는
 * 그냥 직선으로 잇는다.
 */
export function orthoPath(pts) {
  if (!pts?.length) return "";
  let d = `M ${round2(pts[0].x)} ${round2(pts[0].y)}`;
  for (let i = 1; i < pts.length - 1; i += 1) {
    const prev = pts[i - 1];
    const cur = pts[i];
    const next = pts[i + 1];
    const rIn = dist(prev, cur);
    const rOut = dist(cur, next);
    const r = Math.min(CORNER_R, rIn / 2, rOut / 2);
    if (r < 0.5) {
      d += ` L ${round2(cur.x)} ${round2(cur.y)}`;
      continue;
    }
    const dIn = { x: sign(cur.x - prev.x), y: sign(cur.y - prev.y) };
    const dOut = { x: sign(next.x - cur.x), y: sign(next.y - cur.y) };
    const p1 = { x: cur.x - dIn.x * r, y: cur.y - dIn.y * r };
    const p2 = { x: cur.x + dOut.x * r, y: cur.y + dOut.y * r };
    const cross = dIn.x * dOut.y - dIn.y * dOut.x;
    const sweep = cross > 0 ? 1 : 0;
    d += ` L ${round2(p1.x)} ${round2(p1.y)} A ${round2(r)} ${round2(r)} 0 0 ${sweep} ${round2(p2.x)} ${round2(p2.y)}`;
  }
  const last = pts[pts.length - 1];
  d += ` L ${round2(last.x)} ${round2(last.y)}`;
  return d;
}

/** 점 열 전체에서 표본점 n 개(전수 검사·시각 확인용) — 구간마다 균등 등분한다(전부 직선이라 쉽다). */
export function samplePoints(pts, n = 20) {
  if (!pts || pts.length < 2) return pts?.length ? [pts[0]] : [];
  const segs = pts.length - 1;
  const per = Math.max(1, Math.ceil(n / segs));
  const out = [];
  for (let i = 0; i < segs; i += 1) {
    const a = pts[i];
    const b = pts[i + 1];
    for (let j = 0; j < per; j += 1) {
      const t = (j + 0.5) / per;
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  }
  return out.slice(0, n);
}

/** 점 열의 수평 구간만 골라낸다 — 칩 배치·공선 겹침 검사가 함께 쓴다. */
function horizontalSegs(pts) {
  const out = [];
  for (let i = 0; i < pts.length - 1; i += 1) {
    const a = pts[i];
    const b = pts[i + 1];
    if (Math.abs(a.y - b.y) < 0.01) out.push({ i, y: a.y, xLo: Math.min(a.x, b.x), xHi: Math.max(a.x, b.x) });
  }
  return out;
}

/**
 * s->t 정방향 호의 열 번호·y 정지점 열 — 방향전환(트랙) 자리를 정하기 전 위상만 담는다.
 * outIdx·outN·inIdx·inN 은 이후 snapTinyJogs 가 포트 밴드·최소 간격을 판정할 때 쓴다.
 */
function forwardStops(edge, s, t, geometry) {
  const outN = edge.po != null ? s.outN : 1;
  const inN = edge.pi != null ? t.inN : 1;
  const outIdx = edge.po || 0;
  const inIdx = edge.pi || 0;
  const cols = [s.c];
  const ys = [portY(s, outIdx, outN, "out")];
  const wpLane = geometry.laneByKey[t.node.lane];
  for (const [c, yU] of edge.w || []) {
    cols.push(c);
    ys.push(wpLane.top + LANE_PAD + yU * U);
  }
  cols.push(t.c);
  ys.push(portY(t, inIdx, inN, "in"));
  return { cols, ys, back: false, sKey: s.key, tKey: t.key, outIdx, outN, inIdx, inN };
}

/** 되돌이 호의 정지점 — [출발 y, 채널 y, 도착 y] 세 점, 틈은 s.c(다음 틈)와 t.c-1(도착 앞 틈) 두 곳. */
function backStops(edge, s, t, geometry) {
  const outN = edge.po != null ? s.outN : 1;
  const inN = edge.pi != null ? t.inN : 1;
  const outIdx = edge.po || 0;
  const inIdx = edge.pi || 0;
  const ys0 = portY(s, outIdx, outN, "out");
  const yt0 = portY(t, inIdx, inN, "in");
  const chLane = geometry.laneByKey[t.node.lane];
  const chY = chLane.top + LANE_PAD + (edge.ch || 0) * U;
  return {
    cols: [s.c, s.c + 1, t.c - 1, t.c], ys: [ys0, chY, chY, yt0], back: true, gapCols: [s.c, t.c - 1],
    sKey: s.key, tKey: t.key, outIdx, outN, inIdx, inN,
  };
}

/**
 * 경유점 idx 와 같은 y(수평 한 줄)를 이루는 이웃 경유점들의 인덱스 — 양옆으로 값이 갈리는
 * 자리(포트 포함)에서 멈춘다. 이 묶음을 한 번에 옮겨야 한쪽만 밀어 다른 쪽에 새 jog 가
 * 안 생긴다(경유점 여러 칸이 같은 행을 그대로 지나는 구간을 흔히 만든다).
 */
function waypointRun(st, idx) {
  const last = st.ys.length - 1;
  const y0 = st.ys[idx];
  let lo = idx;
  let hi = idx;
  while (lo - 1 > 0 && Math.abs(st.ys[lo - 1] - y0) < 0.5) lo -= 1;
  while (hi + 1 < last && Math.abs(st.ys[hi + 1] - y0) < 0.5) hi += 1;
  const out = [];
  for (let i = lo; i <= hi; i += 1) out.push(i);
  return out;
}

/**
 * 정지점 idx 를 옮길 때 함께 옮겨야 할 인덱스들 — 되돌이 호의 채널(정지점 1·2)은 한 값을
 * 공유해 늘 같이 움직이고, 그 외 경유점은 같은 y 를 이룬 이웃 구간을 통째로 묶는다.
 * 포트(idx 0·last)는 자기 자신뿐이다.
 */
function linkedStopIdx(st, idx) {
  const last = st.ys.length - 1;
  if (st.back && (idx === 1 || idx === 2)) return [1, 2];
  if (idx > 0 && idx < last) return waypointRun(st, idx);
  return [idx];
}

/** 정지점 idx 의 y 를 newY 로 옮긴다 — 되돌이 채널은 짝 정지점도 함께 맞춘다. */
function setStopY(st, idx, newY) {
  for (const i of linkedStopIdx(st, idx)) st.ys[i] = newY;
}

/** 같은 노드·같은 면(출발/도착)에서 포트 idx 를 뺀 나머지 포트들의 현재 y — 스냅 중에도 최신값을 읽는다. */
function siblingPortYs(stopsList, nodeKey, side, excludeIdx) {
  const ys = [];
  for (const st of stopsList) {
    if (!st) continue;
    if (side === "out" && st.sKey === nodeKey && st.outIdx !== excludeIdx) ys.push(st.ys[0]);
    if (side === "in" && st.tKey === nodeKey && st.inIdx !== excludeIdx) ys.push(st.ys[st.ys.length - 1]);
  }
  return ys;
}

/**
 * 정지점 하나를 newY 로 스냅해도 되는지 보고, 되면 실제로 옮긴다.
 * 끝 정지점(출발·도착 포트)은 그 면의 포트 밴드 안에 있어야 하고, 옆 포트와 최소 간격(minPortGap)을
 * 지켜야 허용한다. 가운데(경유점·되돌이 채널)는 제약이 없어 늘 옮길 수 있다.
 */
function trySnapStop(stopsList, geometry, st, idx, newY) {
  const last = st.ys.length - 1;
  if (idx === 0 || idx === last) {
    const isSource = idx === 0;
    const g = geometry.nodes[isSource ? st.sKey : st.tKey];
    if (!g) return false;
    if (Math.abs(newY - g.yA) > PORT_BAND / 2 + 0.01) return false;
    const side = isSource ? "out" : "in";
    const n = isSource ? st.outN : st.inN;
    const portIdx = isSource ? st.outIdx : st.inIdx;
    const minGap = minPortGap(side, n);
    if (minGap > 0) {
      const others = siblingPortYs(stopsList, isSource ? st.sKey : st.tKey, side, portIdx);
      if (others.some((y) => Math.abs(y - newY) < minGap - 0.01)) return false;
    }
  }
  setStopY(st, idx, newY);
  return true;
}

/**
 * 아주 작은 dy(0<|dy|<10px)로 생기는 흔들림(wobble)을 없앤다 — 포트가 퍼지며 생기는 짧은 지그재그다.
 * |dy| 가 작은 순서로 처리해, 먼저 스냅한 값이 다음 판정의 기준이 되게 한다.
 * 도착(마지막 정지점)을 먼저 옮겨 보고, 밴드·간격 때문에 안 되면 반대쪽(출발 또는 경유점)을 옮긴다.
 * 경유점끼리의 구간은 뒤쪽을 앞쪽에 맞춘다 — 자유 정지점이라 늘 허용된다.
 * 어느 쪽도 안 되면(포트 제약을 둘 다 어기면) 그 호는 그대로 둔다.
 */
function snapTinyJogs(stopsList, geometry) {
  const candidates = [];
  stopsList.forEach((st, ei) => {
    if (!st) return;
    for (let i = 0; i < st.ys.length - 1; i += 1) {
      const dy = Math.abs(st.ys[i + 1] - st.ys[i]);
      if (dy > 0 && dy < 10) candidates.push({ ei, i, dy });
    }
  });
  candidates.sort((a, b) => a.dy - b.dy);

  for (const c of candidates) {
    const st = stopsList[c.ei];
    const last = st.ys.length - 1;
    const dy = Math.abs(st.ys[c.i + 1] - st.ys[c.i]);
    if (dy <= 0 || dy >= 10) continue; // 앞선 스냅으로 이미 없어졌거나 더는 대상이 아니다.
    if (c.i + 1 === last) {
      if (trySnapStop(stopsList, geometry, st, last, st.ys[c.i])) continue;
      trySnapStop(stopsList, geometry, st, c.i, st.ys[last]);
    } else {
      trySnapStop(stopsList, geometry, st, c.i + 1, st.ys[c.i]);
    }
  }
}

/**
 * 방향전환(트랙) 목록 — stops 의 연속한 두 정지점 사이 |dy|>=0.5 인 자리만 튼다.
 * 되돌이는 s.c·t.c-1 두 틈에서만(중간 채널 구간은 수평 한 줄이라 트랙이 필요 없다).
 */
function collectJogs(stopsList) {
  const jogs = [];
  stopsList.forEach((st) => {
    const { ei } = st;
    if (st.back) {
      const [gc0, gc1] = st.gapCols;
      if (Math.abs(st.ys[1] - st.ys[0]) >= 0.5) jogs.push({ ei, stopIdx: 0, gapC: gc0, y0: st.ys[0], y1: st.ys[1] });
      if (Math.abs(st.ys[3] - st.ys[2]) >= 0.5) jogs.push({ ei, stopIdx: 2, gapC: gc1, y0: st.ys[2], y1: st.ys[3] });
      return;
    }
    for (let i = 0; i < st.cols.length - 1; i += 1) {
      if (Math.abs(st.ys[i + 1] - st.ys[i]) >= 0.5) {
        jogs.push({
          ei, stopIdx: i, gapC: st.cols[i], y0: st.ys[i], y1: st.ys[i + 1], sKey: st.sKey, tKey: st.tKey,
        });
      }
    }
  });
  return jogs;
}

/**
 * 한 틈 안의 방향전환들을 트랙 순서(왼쪽->오른쪽)로 정한다 — 교차를 줄이는 순서다.
 *   하행(y1>y0): y0 가 클수록 더 왼쪽 트랙(일찍 꺾인다). 상행(y1<y0): y0 가 작을수록 더 왼쪽.
 *   하행 묶음·상행 묶음은 각각 안에서 순서가 고정이고, 둘 중 어느 쪽을 왼쪽에 둘지는
 *   두 배치의 "교차 추정치"(도착 y1 을 트랙 순서에 놓았을 때 역전 수)를 비교해 더 적은 쪽을 고른다.
 */
function orderGapJogs(items) {
  const down = items.filter((it) => it.y1 > it.y0).sort((a, b) => b.y0 - a.y0);
  const up = items.filter((it) => it.y1 < it.y0).sort((a, b) => a.y0 - b.y0);
  const score = (arr) => {
    let c = 0;
    for (let i = 0; i < arr.length; i += 1) {
      for (let j = i + 1; j < arr.length; j += 1) {
        if ((arr[i].y1 - arr[j].y1) * (i - j) < 0) c += 1;
      }
    }
    return c;
  };
  const optA = [...down, ...up];
  const optB = [...up, ...down];
  return score(optA) <= score(optB) ? optA : optB;
}

/**
 * 한 틈 안에서 트랙을 나눠 쓸 묶음 키 — 같은 목표로 모이는 방향전환이 둘 이상이면 목표 쪽으로,
 * 아니면 같은 출발에서 갈라지는 방향전환이 둘 이상일 때 출발 쪽으로 묶는다. 되돌이는 묶지 않는다.
 */
function bundleKeys(items) {
  const inCount = new Map();
  const outCount = new Map();
  for (const it of items) {
    if (!it.tKey) continue;
    inCount.set(it.tKey, (inCount.get(it.tKey) || 0) + 1);
    outCount.set(it.sKey, (outCount.get(it.sKey) || 0) + 1);
  }
  const keyOf = new Map();
  for (const it of items) {
    if (!it.tKey) continue;
    if (inCount.get(it.tKey) >= 2) keyOf.set(it, `in:${it.tKey}`);
    else if (outCount.get(it.sKey) >= 2) keyOf.set(it, `out:${it.sKey}`);
  }
  return keyOf;
}

/**
 * 트랙 자리(slot) 목록 — 트랙 순서를 따르되,
 * 한 묶음 안에서 세로 구간이 서로 떨어진 방향전환은 한 자리를 나눠 쓴다.
 * 위에서 내려오는 호와 아래에서 올라오는 호가 한 목표로 모이면,
 * 세로 구간이 겹치지 않아 같은 x 에서 꺾어도 된다.
 * 세로 구간이 겹치는 것끼리는(둘 다 위에서 내려오는 경우 등) 자리를 따로 둔다.
 */
function trackSlots(ordered) {
  const keyOf = bundleKeys(ordered);
  const slots = [];
  const open = new Map();
  for (const it of ordered) {
    const lo = Math.min(it.y0, it.y1);
    const hi = Math.max(it.y0, it.y1);
    const key = keyOf.get(it);
    const cands = key ? open.get(key) || [] : [];
    const apart = ([a, b]) => b + TRACK_SHARE_GAP <= lo || hi + TRACK_SHARE_GAP <= a;
    const slot = cands.find((sl) => sl.spans.every(apart));
    if (slot) {
      slot.items.push(it);
      slot.spans.push([lo, hi]);
      continue;
    }
    const fresh = { items: [it], spans: [[lo, hi]] };
    slots.push(fresh);
    if (key) open.set(key, [...cands, fresh]);
  }
  return slots;
}

/** 모든 틈의 트랙 x 를 정한다 — jogKey(ei,stopIdx) -> x. */
function assignTracks(jogs) {
  const byGap = new Map();
  for (const j of jogs) {
    const key = j.gapC;
    if (!byGap.has(key)) byGap.set(key, []);
    byGap.get(key).push(j);
  }
  const trackX = new Map();
  for (const [, items] of byGap) {
    const slots = trackSlots(orderGapJogs(items));
    const n = slots.length;
    const lo0 = Math.min(...items.map((it) => it.gapLo));
    const hi0 = Math.max(...items.map((it) => it.gapHi));
    const mid = (lo0 + hi0) / 2;
    const usableHalf = Math.max(0, (hi0 - lo0) / 2 - CURVE_MARGIN);
    const step = n > 1 ? Math.min(CURVE_STAGGER, (usableHalf * 2) / (n - 1)) : 0;
    slots.forEach((slot, k) => {
      const x = n > 1 ? mid + (k - (n - 1) / 2) * step : mid;
      for (const it of slot.items) trackX.set(`${it.ei}:${it.stopIdx}`, x);
    });
  }
  return trackX;
}

/** 정방향 호의 점 열 — stops(정지점) + 트랙 x 로 조립한다. */
function buildForwardPts(s, t, stops, trackX) {
  const pts = [{ x: sideX(s, "right", stops.ys[0]), y: stops.ys[0] }];
  for (let i = 0; i < stops.cols.length - 1; i += 1) {
    const tx = trackX.get(`${stops.ei}:${i}`);
    if (tx == null) continue;
    const y0 = stops.ys[i];
    const y1 = stops.ys[i + 1];
    pts.push({ x: tx, y: y0 });
    pts.push({ x: tx, y: y1 });
  }
  const endY = stops.ys[stops.ys.length - 1];
  const endX = sideX(t, "left", endY);
  const last = pts[pts.length - 1];
  if (last.x !== endX || last.y !== endY) pts.push({ x: endX, y: endY });
  return pts;
}

/** 되돌이 호의 점 열 — 출발 오른쪽 -> [트랙1] 채널 y -> 채널 수평선 -> [트랙2] -> 도착 왼쪽. */
function buildBackPts(s, t, stops, trackX) {
  const ys0 = stops.ys[0];
  const chY = stops.ys[1];
  const yt0 = stops.ys[3];
  const pts = [{ x: sideX(s, "right", ys0), y: ys0 }];
  const tx1 = trackX.get(`${stops.ei}:0`);
  if (tx1 != null) {
    pts.push({ x: tx1, y: ys0 });
    pts.push({ x: tx1, y: chY });
  }
  const tx2 = trackX.get(`${stops.ei}:2`);
  const chStartX = pts[pts.length - 1].x;
  if (tx2 != null) {
    if (chStartX !== tx2) pts.push({ x: tx2, y: chY });
    pts.push({ x: tx2, y: yt0 });
  }
  const endX = sideX(t, "left", yt0);
  const last = pts[pts.length - 1];
  if (last.x !== endX || last.y !== yt0) pts.push({ x: endX, y: yt0 });
  return pts;
}

/**
 * 공선(같은 y, x 겹침) 겹침을 찾아 한쪽 포트를 ±NUDGE 만큼 밀어 깬다 — 전형적인 경우는
 * "B 는 y 에서 떠나고 A 는 같은 y 로 도착"할 때 두 수평 구간이 겹치는 것이다. 트랙 배정은
 * 건드리지 않고(이미 교차를 줄이도록 정해졌다) 정지점 y 하나만 살짝 옮긴 뒤 그 호만 다시 짓는다.
 * 걸린 구간이 그 호의 출발 쪽 절반에 있으면 출발 정지점을, 도착 쪽 절반에 있으면 도착
 * 정지점을 민다 — 항상 도착만 밀면 출발 포트가 원인인 겹침(같은 레인의 다른 노드와 출발
 * 포트 y 가 우연히 같은 경우)은 몇 번을 반복해도 못 없앤다.
 * 최대 몇 차례만 반복한다 — 실 데이터에서 정확히 같은 y 가 겹치는 경우는 드물다.
 */
function repairCollinear(edgesPts, rebuild) {
  for (let pass = 0; pass < 8; pass += 1) {
    const pieces = [];
    edgesPts.forEach((pts, ei) => {
      if (!pts) return;
      for (const seg of horizontalSegs(pts)) {
        const near = seg.i < (pts.length - 1) / 2 ? "first" : "last";
        pieces.push({ ei, near, ...seg });
      }
    });
    pieces.sort((a, b) => a.y - b.y);
    let fixed = false;
    for (let i = 0; i < pieces.length && !fixed; i += 1) {
      for (let j = i + 1; j < pieces.length && pieces[j].y - pieces[i].y < COLLINEAR_EPS; j += 1) {
        const p = pieces[i];
        const q = pieces[j];
        if (p.ei === q.ei) continue;
        if (p.xLo < q.xHi - 0.01 && q.xLo < p.xHi - 0.01) {
          rebuild(q.ei, NUDGE * (q.ei % 2 === 0 ? 1 : -1), q.near);
          edgesPts[q.ei] = rebuild.lastPts;
          fixed = true;
          break;
        }
      }
    }
    if (!fixed) break;
  }
}

/** 두 사각형(x,y,w,h, 좌상단 기준) 겹침 판정 — 경계가 맞닿기만 하면 겹침으로 안 본다. */
function rectsOverlap(a, b) {
  return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
}

/** 점 열의 모든 구간(수평·수직 가리지 않음)을 사각형으로 — 칩이 다른 호와 겹치는지 볼 때 쓴다. */
function segRects(pts) {
  const out = [];
  for (let i = 0; i < pts.length - 1; i += 1) {
    const a = pts[i];
    const b = pts[i + 1];
    out.push({
      x: Math.min(a.x, b.x), y: Math.min(a.y, b.y),
      w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y),
    });
  }
  return out;
}

/** nodeBBox(x0,x1,top,bottom) -> 사각형(x,y,w,h) — 칩 충돌 판정을 rectsOverlap 하나로 통일한다. */
function nodeRect(box) {
  return { x: box.x0, y: box.top, w: box.x1 - box.x0, h: box.bottom - box.top };
}

// 칩 자리를 수평 구간을 따라 옮겨 가며 찾을 때의 보폭(px).
const CHIP_SLIDE_STEP = 6;

/**
 * 수평 구간 하나 안에서 칩 중심 x 후보를 선호 순서대로 낸다.
 * 도착 쪽 마지막 구간은 화살촉 앞 10px 오른쪽 정렬에서 시작해 왼쪽으로,
 * 나머지 구간은 가운데에서 양옆으로 번갈아 옮긴다.
 */
function chipSlots(seg, w, isLast) {
  const lo = seg.xLo + w / 2;
  const hi = seg.xHi - w / 2;
  if (hi < lo) return [clamp((seg.xLo + seg.xHi) / 2, seg.xLo, seg.xHi)];
  const out = [];
  if (isLast) {
    for (let cx = clamp(seg.xHi - 10 - w / 2, lo, hi); cx >= lo; cx -= CHIP_SLIDE_STEP) out.push(cx);
    return out;
  }
  const mid = (lo + hi) / 2;
  out.push(mid);
  for (let d = CHIP_SLIDE_STEP; mid - d >= lo || mid + d <= hi; d += CHIP_SLIDE_STEP) {
    if (mid + d <= hi) out.push(mid + d);
    if (mid - d >= lo) out.push(mid - d);
  }
  return out;
}

/**
 * 호 하나의 q/r 칩 자리 — 기본은 도착 쪽 마지막 수평 구간에 오른쪽 정렬(화살촉 앞 10px),
 * 안 되면 그 구간을 따라 옮겨 보고, 그래도 안 되면 다른 수평 구간을 순서대로 시도한다.
 * 후보 자리(2px 부풀린 칩 사각형)가 이미 놓인 칩, 이 호가 아닌 다른 호의 어떤 구간,
 * 자신의 출발·도착 노드가 아닌 어떤 노드의 도형·라벨 상자와도 안 겹쳐야 그 자리를 쓴다.
 * 전부 실패하면 hidden(값은 title 로).
 */
function placeChip(pts, text, measure, placed, otherSegs, nodeBoxes) {
  const horiz = horizontalSegs(pts);
  if (!horiz.length) return null;
  const w = measure(text) + CHIP_PAD_X;
  const h = CHIP_H;
  const last = horiz[horiz.length - 1];
  const order = [last, ...horiz.slice(0, -1).reverse()];
  for (const seg of order) {
    for (const cx of chipSlots(seg, w, seg === last)) {
      const box = { x: cx - w / 2, y: seg.y - h / 2, w, h };
      const inflated = { x: box.x - 2, y: box.y - 2, w: box.w + 4, h: box.h + 4 };
      const hit = placed.some((o) => rectsOverlap(inflated, o))
        || (otherSegs || []).some((o) => rectsOverlap(inflated, o))
        || (nodeBoxes || []).some((o) => rectsOverlap(inflated, o));
      if (!hit) {
        placed.push(box);
        return { text, x: cx, y: seg.y, w, h, hidden: false };
      }
    }
  }
  const mid = horiz[horiz.length - 1];
  return { text, x: (mid.xLo + mid.xHi) / 2, y: mid.y, w, h, hidden: true };
}

/** 호 하나의 칩 글자 — q 우선, 없으면 r, 둘 다 없으면 빈 문자열. */
function chipTextOf(edge) {
  return edge.q || edge.r || "";
}

/**
 * 같은 목표 노드로 들어오는, 칩 붙은 정방향 호 2개 이상을 한 줄(모자라면 두 줄)로 맞춘다.
 *
 * 도착 포트는 최대 12px(PORT_STEP_MAX_IN) 간격인데 칩은 16px 높이(+부딪힘 판정에서 2px씩
 * 부풀린다)라, 포트가 둘만 있어도 인접 칩이 늘 겹친다 — 그 자리에서 매번 슬라이드로 피하면
 * 먼저 놓인 쪽만 자리를 지키고 나중 쪽만 밀려나 줄이 어긋나 보인다.
 * 대신 이 노드로 들어오는 칩들만 따로 모아, 화살촉 앞 10px 오른쪽 정렬 한 줄로 spacing(칩
 * 높이+4, 포트 밴드가 허용하는 값 중 작은 쪽)만큼 고르게 벌려 놓는다. 그 간격이 칩 높이+4보다
 * 좁아질 만큼(3개 이상) 몰리면, 포트 순번대로 가까운 열·먼 열에 번갈아 담아 같은 열 안에서는
 * 자리가 넉넉히 남게 한다 — 그래도 남는 부딪힘은 buildEdges 의 기존 슬라이드가 개별 호 단위로
 * 마저 피한다(여기서 정한 자리는 "이상적인 자리"일 뿐, 최종 채택 여부는 충돌 검사가 정한다).
 */
function alignArrivalChips(rawEdges, stopsList, geometry, measure) {
  const groups = new Map();
  rawEdges.forEach((edge, ei) => {
    if (edge.back || !stopsList[ei] || !chipTextOf(edge)) return;
    if (!groups.has(edge.t)) groups.set(edge.t, []);
    groups.get(edge.t).push(ei);
  });

  const slots = new Map();
  for (const [targetKey, eis] of groups) {
    if (eis.length < 2) continue;
    const t = geometry.nodes[targetKey];
    if (!t) continue;
    const widths = new Map(eis.map((ei) => [ei, measure(chipTextOf(rawEdges[ei])) + CHIP_PAD_X]));
    const sorted = [...eis].sort((a, b) => {
      const ya = stopsList[a].ys[stopsList[a].ys.length - 1];
      const yb = stopsList[b].ys[stopsList[b].ys.length - 1];
      return ya - yb;
    });
    const n = sorted.length;
    const spacing = Math.min(CHIP_H + 4, PORT_BAND / (n - 1));
    const nearRight = sideX(t, "left", t.yA) - 10;
    if (spacing >= CHIP_H + 4) {
      // 밴드가 넉넉하다 — 노드 앵커선(yA)을 중심으로 한 줄에 고르게 벌린다.
      sorted.forEach((ei, idx) => {
        const w = widths.get(ei);
        const cy = t.yA + (idx - (n - 1) / 2) * spacing;
        slots.set(ei, { cx: nearRight - w / 2, cy, w });
      });
    } else {
      // 밴드가 모자란다 — 포트 순번대로 가까운 열·먼 열에 번갈아 담고, y 는 실제 도착 포트 그대로 둔다.
      const nearW = Math.max(...sorted.filter((_, i) => i % 2 === 0).map((ei) => widths.get(ei)));
      const farRight = nearRight - nearW - CHIP_COL_GAP;
      sorted.forEach((ei, idx) => {
        const w = widths.get(ei);
        const cy = stopsList[ei].ys[stopsList[ei].ys.length - 1];
        const rightX = idx % 2 === 0 ? nearRight : farRight;
        slots.set(ei, { cx: rightX - w / 2, cy, w });
      });
    }
  }
  return slots;
}

/**
 * 모든 엣지의 렌더 서술자(경로 d·점 열·칩)를 만든다.
 * measure 는 칩 글자 폭 측정 함수(기본은 글자 수 추정 — 실제 렌더는 canvas 측정을 넘긴다).
 */
export function buildEdges(spec, geometry, measure = fallbackMeasure) {
  const rawEdges = spec?.edges || [];
  const stopsList = rawEdges.map((edge) => {
    const s = geometry.nodes[edge.s];
    const t = geometry.nodes[edge.t];
    if (!s || !t) return null;
    const st = edge.back ? backStops(edge, s, t, geometry) : forwardStops(edge, s, t, geometry);
    return st;
  });
  stopsList.forEach((st, ei) => { if (st) st.ei = ei; });
  // 트랙을 배정하기 전에 아주 작은 dy(흔들림)부터 없앤다 — 그래야 그 자리에 트랙 자체가 안 생긴다.
  snapTinyJogs(stopsList, geometry);

  const jogs = collectJogs(stopsList.filter(Boolean));
  // 틈의 [왼쪽 x, 오른쪽 x] — gapC 가 -1(lead)·maxCol(trail) 인 그림 바깥 특수 틈도 같은 식으로 구한다.
  jogs.forEach((j) => {
    if (j.gapC < 0) {
      j.gapHi = geometry.colX0(0);
      j.gapLo = j.gapHi - geometry.lead;
    } else if (j.gapC >= geometry.maxCol) {
      j.gapLo = geometry.colX0(geometry.maxCol) + BOX_W;
      j.gapHi = j.gapLo + geometry.trail;
    } else {
      j.gapLo = geometry.colX0(j.gapC) + BOX_W;
      j.gapHi = geometry.colX0(j.gapC + 1);
    }
  });
  const trackX = assignTracks(jogs);

  const buildOne = (ei) => {
    const st = stopsList[ei];
    if (!st) return null;
    const edge = rawEdges[ei];
    const s = geometry.nodes[edge.s];
    const t = geometry.nodes[edge.t];
    return st.back ? buildBackPts(s, t, st, trackX) : buildForwardPts(s, t, st, trackX);
  };

  const edgesPts = stopsList.map((st, ei) => (st ? buildOne(ei) : null));

  // 공선 겹침 보정 — 걸린 호의 해당 정지점 y 를 살짝 밀고 그 호만 다시 짓는다.
  // end="first" 면 출발 정지점을, 그 외("last", 기본)면 도착 정지점을 민다.
  const rebuild = (ei, dy, end = "last") => {
    const st = stopsList[ei];
    if (!st) return;
    const idx = end === "first" ? 0 : st.ys.length - 1;
    st.ys[idx] += dy;
    rebuild.lastPts = buildOne(ei);
  };
  repairCollinear(edgesPts, rebuild);

  // 칩 충돌 판정용 — 모든 호의 구간, 모든 노드의 상자(각 호는 자신의 출발·도착 노드만 뺀다).
  const edgeSegsAll = edgesPts.map((pts) => (pts ? segRects(pts) : []));
  const nodeBoxEntries = Object.values(geometry.nodes).map((g) => ({ key: g.key, rect: nodeRect(nodeBBox(g)) }));
  const arrivalSlots = alignArrivalChips(rawEdges, stopsList, geometry, measure);

  const placedChips = [];
  const out = [];
  rawEdges.forEach((edge, ei) => {
    const pts = edgesPts[ei];
    if (!pts) return;
    const d = orthoPath(pts);
    let chip = null;
    const chipText = chipTextOf(edge);
    if (chipText) {
      const otherSegs = edgeSegsAll.flatMap((segs, j) => (j === ei ? [] : segs));
      const nodeBoxes = nodeBoxEntries.filter((n) => n.key !== edge.s && n.key !== edge.t).map((n) => n.rect);
      const slot = arrivalSlots.get(ei);
      if (slot) {
        const box = { x: slot.cx - slot.w / 2, y: slot.cy - CHIP_H / 2, w: slot.w, h: CHIP_H };
        const inflated = { x: box.x - 2, y: box.y - 2, w: box.w + 4, h: box.h + 4 };
        const hit = placedChips.some((o) => rectsOverlap(inflated, o))
          || otherSegs.some((o) => rectsOverlap(inflated, o))
          || nodeBoxes.some((o) => rectsOverlap(inflated, o));
        if (!hit) {
          placedChips.push(box);
          chip = { text: chipText, x: slot.cx, y: slot.cy, w: slot.w, h: CHIP_H, hidden: false };
        }
      }
      // 정렬 자리가 다른 호·노드와 부딪히면(같은 줄끼리는 이미 안 부딪히게 짰다) 기존 슬라이드로 물러선다.
      if (!chip) chip = placeChip(pts, chipText, measure, placedChips, otherSegs, nodeBoxes);
    }
    out.push({
      key: `${edge.s}-${edge.t}-${out.length}`, edge, pts, d, back: !!edge.back,
      chip, chipType: edge.q ? "q" : (edge.r ? "r" : null), ei,
    });
  });
  return out;
}
